/**
 * Behavioral Event Listeners
 * Tracks detailed mouse, keyboard, touch events with timing data for ML
 */

export interface PointerEventData {
  timestamp: number;
  x: number;
  y: number;
  eventType: 'move' | 'click' | 'touch' | 'scroll';
  velocity?: number;
  acceleration?: number;
}

export interface KeyboardEventData {
  timestamp: number;
  eventType: 'keydown' | 'keyup';
  dwellTimeMs?: number; // Time key was held down
  flightTimeMs?: number; // Time between this key and previous key
  fieldType?: string; // input, textarea, contenteditable, etc.
}

export interface BehavioralData {
  // Aggregated counts (for backward compatibility)
  mouseMovements: number;
  mouseClicks: number;
  touchEvents: number;
  scrollEvents: number;
  focusChanges: number;
  keyPresses: number;

  // Detailed events for ML
  pointerEvents: PointerEventData[];
  keyboardEvents: KeyboardEventData[];
}

class BehavioralTracker {
  private data: BehavioralData = {
    mouseMovements: 0,
    mouseClicks: 0,
    touchEvents: 0,
    scrollEvents: 0,
    focusChanges: 0,
    keyPresses: 0,
    pointerEvents: [],
    keyboardEvents: [],
  };

  private handlers: Array<() => void> = [];

  // State for calculating velocity/acceleration
  private lastMousePos: { x: number; y: number; time: number } | null = null;
  private lastVelocity: number = 0;

  // State for keyboard timing
  private keyDownTimes: Map<string, number> = new Map();
  private lastKeyUpTime: number = 0;

  // Limits to prevent memory issues
  private readonly MAX_POINTER_EVENTS = 500;
  private readonly MAX_KEYBOARD_EVENTS = 200;

  start() {
    if (typeof window === 'undefined') return;

    // Mouse move with velocity/acceleration tracking
    const handleMouseMove = (e: MouseEvent) => {
      this.data.mouseMovements++;

      const now = Date.now();
      const x = e.clientX;
      const y = e.clientY;

      // Calculate velocity and acceleration
      let velocity: number | undefined;
      let acceleration: number | undefined;

      if (this.lastMousePos) {
        const dx = x - this.lastMousePos.x;
        const dy = y - this.lastMousePos.y;
        const dt = now - this.lastMousePos.time;

        if (dt > 0) {
          const distance = Math.sqrt(dx * dx + dy * dy);
          velocity = distance / dt; // pixels per ms

          // Calculate acceleration
          acceleration = (velocity - this.lastVelocity) / dt;
          this.lastVelocity = velocity;
        }
      }

      this.lastMousePos = { x, y, time: now };

      // Store detailed event (with limit)
      if (this.data.pointerEvents.length < this.MAX_POINTER_EVENTS) {
        this.data.pointerEvents.push({
          timestamp: now,
          x,
          y,
          eventType: 'move',
          velocity,
          acceleration,
        });
      }
    };

    // Mouse click
    const handleClick = (e: MouseEvent) => {
      this.data.mouseClicks++;

      if (this.data.pointerEvents.length < this.MAX_POINTER_EVENTS) {
        this.data.pointerEvents.push({
          timestamp: Date.now(),
          x: e.clientX,
          y: e.clientY,
          eventType: 'click',
        });
      }
    };

    // Touch events
    const handleTouchStart = (e: TouchEvent) => {
      this.data.touchEvents++;

      if (e.touches.length > 0 && this.data.pointerEvents.length < this.MAX_POINTER_EVENTS) {
        const touch = e.touches[0];
        this.data.pointerEvents.push({
          timestamp: Date.now(),
          x: touch.clientX,
          y: touch.clientY,
          eventType: 'touch',
        });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      this.data.touchEvents++;

      if (e.touches.length > 0 && this.data.pointerEvents.length < this.MAX_POINTER_EVENTS) {
        const touch = e.touches[0];
        this.data.pointerEvents.push({
          timestamp: Date.now(),
          x: touch.clientX,
          y: touch.clientY,
          eventType: 'touch',
        });
      }
    };

    // Scroll events
    const handleScroll = () => {
      this.data.scrollEvents++;

      if (this.data.pointerEvents.length < this.MAX_POINTER_EVENTS) {
        this.data.pointerEvents.push({
          timestamp: Date.now(),
          x: window.scrollX,
          y: window.scrollY,
          eventType: 'scroll',
        });
      }
    };

    // Focus events
    const handleFocus = () => {
      this.data.focusChanges++;
    };

    // Keyboard down - track timing
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.code || e.key;
      const now = Date.now();

      // Don't track if already down (repeated keydown events)
      if (!this.keyDownTimes.has(key)) {
        this.keyDownTimes.set(key, now);

        // Get field type if typing in an input
        let fieldType: string | undefined;
        const target = e.target as HTMLElement;
        if (target) {
          if (target.tagName === 'INPUT') {
            fieldType = (target as HTMLInputElement).type || 'text';
          } else if (target.tagName === 'TEXTAREA') {
            fieldType = 'textarea';
          } else if (target.isContentEditable) {
            fieldType = 'contenteditable';
          }
        }

        if (this.data.keyboardEvents.length < this.MAX_KEYBOARD_EVENTS) {
          this.data.keyboardEvents.push({
            timestamp: now,
            eventType: 'keydown',
            fieldType,
          });
        }
      }
    };

    // Keyboard up - calculate dwell and flight time
    const handleKeyUp = (e: KeyboardEvent) => {
      this.data.keyPresses++;

      const key = e.code || e.key;
      const now = Date.now();
      const downTime = this.keyDownTimes.get(key);

      let dwellTimeMs: number | undefined;
      let flightTimeMs: number | undefined;

      if (downTime !== undefined) {
        dwellTimeMs = now - downTime;
        this.keyDownTimes.delete(key);
      }

      if (this.lastKeyUpTime > 0) {
        flightTimeMs = now - this.lastKeyUpTime;
      }

      this.lastKeyUpTime = now;

      // Get field type
      let fieldType: string | undefined;
      const target = e.target as HTMLElement;
      if (target) {
        if (target.tagName === 'INPUT') {
          fieldType = (target as HTMLInputElement).type || 'text';
        } else if (target.tagName === 'TEXTAREA') {
          fieldType = 'textarea';
        } else if (target.isContentEditable) {
          fieldType = 'contenteditable';
        }
      }

      if (this.data.keyboardEvents.length < this.MAX_KEYBOARD_EVENTS) {
        this.data.keyboardEvents.push({
          timestamp: now,
          eventType: 'keyup',
          dwellTimeMs,
          flightTimeMs,
          fieldType,
        });
      }
    };

    // Register listeners
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('click', handleClick, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('focus', handleFocus, true);
    window.addEventListener('blur', handleFocus, true);
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    window.addEventListener('keyup', handleKeyUp, { passive: true });

    // Store cleanup handlers
    this.handlers = [
      () => window.removeEventListener('mousemove', handleMouseMove),
      () => window.removeEventListener('click', handleClick),
      () => window.removeEventListener('touchstart', handleTouchStart),
      () => window.removeEventListener('touchmove', handleTouchMove),
      () => window.removeEventListener('scroll', handleScroll),
      () => window.removeEventListener('focus', handleFocus, true),
      () => window.removeEventListener('blur', handleFocus, true),
      () => window.removeEventListener('keydown', handleKeyDown),
      () => window.removeEventListener('keyup', handleKeyUp),
    ];
  }

  stop() {
    this.handlers.forEach((cleanup) => cleanup());
    this.handlers = [];
  }

  getData(): BehavioralData {
    return { ...this.data };
  }

  reset() {
    this.data = {
      mouseMovements: 0,
      mouseClicks: 0,
      touchEvents: 0,
      scrollEvents: 0,
      focusChanges: 0,
      keyPresses: 0,
      pointerEvents: [],
      keyboardEvents: [],
    };

    // Reset tracking state
    this.lastMousePos = null;
    this.lastVelocity = 0;
    this.keyDownTimes.clear();
    this.lastKeyUpTime = 0;
  }
}

// Singleton instance
export const behavioralTracker = new BehavioralTracker();
